/* eslint-disable @typescript-eslint/no-unsafe-call */
import clsx from 'clsx';
import { type FormikHelpers, FormikProvider, useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import * as Yup from 'yup';
import { StepperComponent } from '../../../_metronic/assets/ts/components';
import { KTIcon } from '../../../_metronic/helpers';
import { ToolbarWrapper } from '../../../_metronic/layout/components/toolbar';
import { type ApiResponseError } from '../../../api/core/_models';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { initialSetupRequestEmpty, type SetupRequestDto } from '../../models/SetupRequestDto';
import Step1 from './components/Step1';
import Step2 from './components/Step2';
import Step3 from './components/Step3';
import Step4 from './components/Step4';
import { getSetupDetails, setupCompany } from './core/_requests';

const step1Schema = Yup.object().shape({
  social_name: Yup.string().required('Razão social é obrigatório'),
  fantasy_name: Yup.string().required('Nome fantasia é obrigatório'),
  person_type: Yup.object().required('Tipo de pessoa é obrigatório'),
  cnpj: Yup.string().when('person_type.value', {
    is: 'LEGAL',
    then: (schema) => schema.required('CNPJ é obrigatório'),
    otherwise: (schema) => schema.notRequired(),
  }),
  cpf: Yup.string().when('person_type.value', {
    is: 'PHYSICAL',
    then: (schema) => schema.required('CPF é obrigatório'),
    otherwise: (schema) => schema.notRequired(),
  }),
  telephone: Yup.string().required('Telefone é obrigatório'),
  tax_regime: Yup.object().required('Regime tributário é obrigatório'),
  address: Yup.object().shape({
    street: Yup.string().required('Rua é obrigatório'),
    number: Yup.string().required('Número é obrigatório'),
    neighborhood: Yup.string().required('Bairro é obrigatório'),
    zip_code: Yup.string().required('CEP é obrigatório'),
    city: Yup.string().required('Cidade é obrigatório'),
    state: Yup.object().required('Estado é obrigatório'),
  }),
});

const step2Schema = Yup.object().shape({
  administrator_email: Yup.string().required('E-mail do administrador é obrigatório'),
  administrator_password: Yup.string()
    .min(6, 'A senha deve ter no mínimo 6 caracteres')
    .required('Senha do administrador é obrigatório'),
});

const createAccountSchemas = [step1Schema, step2Schema];

const SetupPage = () => {
  const stepperRef = useRef<HTMLDivElement | null>(null);
  const [stepper, setStepper] = useState<StepperComponent | null>(null);
  const [currentSchema, setCurrentSchema] = useState(createAccountSchemas[0]);
  const [isLoading, setIsLoading] = useState(true);
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const setupMutation = useMutation({
    mutationFn: (data: SetupRequestDto) => setupCompany(data),
    onError: (error) => {
      SystemNotification.error(getErrorMessage(error));
    },
  });

  const setupDetailsQuery = useQuery({
    queryKey: ['setupPage'],
    queryFn: async () => {
      const response = await getSetupDetails();
      stepper?.goto(4);
      return response.data;
    },
    retry: false,
    enabled: !!stepper,
    // 404 means setup not done yet — handled below, no global toast
    meta: { suppressGlobalError: true },
  });

  useEffect(() => {
    if (setupDetailsQuery.isError) {
      const error = setupDetailsQuery.error as unknown as ApiResponseError;
      if (error.status === 404) {
        stepper?.goto(1);
      }
    }
    if (setupDetailsQuery.isSuccess || setupDetailsQuery.isError) {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupDetailsQuery.isSuccess, setupDetailsQuery.isError]);

  const loadStepper = () => {
    setStepper(StepperComponent.createInsance(stepperRef.current as HTMLDivElement));
  };

  const prevStep = () => {
    if (!stepper) return;

    stepper.goPrev();
    // Atualiza o schema com base no índice do step (subtrai 1, pois o índice começa em 0)
    setCurrentSchema(createAccountSchemas[stepper.currentStepIndex - 1]);
  };

  const submitStep = (values: SetupRequestDto, actions: FormikHelpers<SetupRequestDto>) => {
    if (!stepper) return;

    // No step 2, verificar se as senhas são iguais
    if (stepper.currentStepIndex === 2) {
      if (values.administrator_password !== passwordConfirmation) {
        SystemNotification.error('As senhas não conferem');
        return;
      }
    }

    // Se estivermos no último step do fluxo validado, faça a submissão final
    if (stepper.currentStepIndex === stepper.totalStepsNumber - 1) {
      setupMutation.mutate(values);
      stepper.goNext();
      return;
    }

    // Avança para o próximo passo
    if (stepper.currentStepIndex !== stepper.totalStepsNumber) {
      stepper.goNext();
    } else {
      stepper.goto(1);
      actions.resetForm();
    }

    // Atualiza o schema de validação para o novo step
    setCurrentSchema(createAccountSchemas[stepper.currentStepIndex - 1]);
  };

  const formik = useFormik<SetupRequestDto>({
    initialValues: initialSetupRequestEmpty,
    validationSchema: currentSchema,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: submitStep,
  });

  useEffect(() => {
    if (!stepperRef.current) return;
    loadStepper();
  }, [stepperRef]);

  return (
    <>
      {!!isLoading && (
        <div className="d-flex flex-column flex-center flex-column-fluid h-100">
          <div className="text-center">
            <div className="text-gray-400 fw-bold fs-3 mb-5">Carregando...</div>
          </div>
        </div>
      )}
      <ToolbarWrapper />
      <div
        ref={stepperRef}
        className={clsx(
          isLoading && 'invisible',
          'stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid',
        )}
        id="kt_create_account_stepper"
      >
        {/* begin::Aside */}
        <div
          className="d-flex justify-content-center align-items-center flex-row-auto w-100 w-xl-400px w-xxl-400px me-9"
          style={{ background: '#006CEA' }}
        >
          {/* begin::Wrapper */}
          <div className="px-6 px-lg-10 px-xxl-15 py-20">
            {/* begin::Nav */}
            <div className="stepper-nav">
              {/* Step 1 */}
              <div className="stepper-item current" data-kt-stepper-element="nav">
                <div className="stepper-wrapper">
                  <div className="stepper-icon w-40px h-40px">
                    <i className="stepper-check fas fa-check" />
                    <span className="stepper-number">1</span>
                  </div>
                  <div className="stepper-label">
                    <h3 className="stepper-title">Dados da sua empresa</h3>
                    <div className="stepper-desc fw-light">Informações sobre sua empresa</div>
                  </div>
                </div>
                <div className="stepper-line h-40px" />
              </div>
              {/* Step 2 */}
              <div className="stepper-item" data-kt-stepper-element="nav">
                <div className="stepper-wrapper">
                  <div className="stepper-icon w-40px h-40px">
                    <i className="stepper-check fas fa-check" />
                    <span className="stepper-number">2</span>
                  </div>
                  <div className="stepper-label">
                    <h3 className="stepper-title">Configurações de acesso</h3>
                    <div className="stepper-desc fw-light">Configure o acesso administrador</div>
                  </div>
                </div>
                <div className="stepper-line h-40px" />
              </div>
              {/* Step 3 */}
              <div className="stepper-item" data-kt-stepper-element="nav">
                <div className="stepper-wrapper">
                  <div className="stepper-icon w-40px h-40px">
                    <i className="stepper-check fas fa-check" />
                    <span className="stepper-number">3</span>
                  </div>
                  <div className="stepper-label">
                    <h3 className="stepper-title">Logo e estilo</h3>
                    <div className="stepper-desc fw-light">Deixe o sistema com a cara da sua empresa</div>
                  </div>
                </div>
                <div className="stepper-line h-40px" />
              </div>
              {/* Step 4 */}
              <div className="stepper-item" data-kt-stepper-element="nav">
                <div className="stepper-wrapper">
                  <div className="stepper-icon w-40px h-40px">
                    <i className="stepper-check fas fa-check" />
                    <span className="stepper-number">4</span>
                  </div>
                  <div className="stepper-label">
                    <h3 className="stepper-title">Finalizado</h3>
                    <div className="stepper-desc fw-light">Oba, sistema pronto para uso!</div>
                  </div>
                </div>
              </div>
            </div>
            {/* end::Nav */}
          </div>
          {/* end::Wrapper */}
        </div>
        {/* end::Aside */}

        <div className="d-flex flex-row-fluid bg-body rounded">
          <FormikProvider value={formik}>
            <form
              className="w-100 px-6 py-8 py-sm-14 px-sm-14"
              id="kt_create_account_form"
              onSubmit={formik.handleSubmit}
            >
              <div className="current" data-kt-stepper-element="content">
                <Step1 formik={formik} />
              </div>

              <div data-kt-stepper-element="content">
                <Step2
                  formik={formik}
                  passwordConfirmation={passwordConfirmation}
                  setIsPasswordValid={setIsPasswordValid}
                  setPasswordConfirmation={setPasswordConfirmation}
                />
              </div>

              <div data-kt-stepper-element="content">
                <Step3 formik={formik} />
              </div>

              <div data-kt-stepper-element="content">
                <Step4 />
              </div>

              {stepper?.currentStepIndex && stepper.currentStepIndex < 4 ? (
                <div className="d-flex flex-stack pt-10">
                  <div className="mr-2">
                    <button
                      className="btn btn-lg btn-light-primary me-3"
                      data-kt-stepper-action="previous"
                      type="button"
                      onClick={prevStep}
                    >
                      <KTIcon className="fs-4 me-1" iconName="arrow-left" />
                      Voltar
                    </button>
                  </div>
                  <div>
                    <button
                      className="btn btn-lg btn-primary me-3"
                      disabled={stepper.currentStepIndex === 2 && !isPasswordValid}
                      type="submit"
                    >
                      <span className="indicator-label">
                        {stepper.currentStepIndex !== (stepper.totalStepsNumber || 2) - 1 && 'Continuar'}
                        {stepper.currentStepIndex === (stepper.totalStepsNumber || 2) - 1 && 'Finalizar'}
                        <KTIcon className="fs-3 ms-2 me-0" iconName="arrow-right" />
                      </span>
                    </button>
                  </div>
                </div>
              ) : null}
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  );
};

export default SetupPage;
